/* eslint-disable @typescript-eslint/no-explicit-any */
import { changeLocale } from "@shared/actions";
import { useLocale } from "@shared/hooks/language/languages";
import { useLocaleList } from "@shared/hooks/uses-services";
import { appLanguage } from "@shared/i18n";
import { root } from "@shared/utils";
import classNames from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

export const LanguagePopup: React.FC = () => {
  const [isPopupOpen, setPopupOpen] = useState<boolean>(false);
  const locale = useLocale();
  const dispatch = useDispatch();
  const { localeList } = useLocaleList();
  const dropdownRef = useRef<HTMLLIElement | null>(null);
  const isToggleDisabled = localeList.length <= 1;

  const togglePopup = (): void => {
    if (!isToggleDisabled) {
      setPopupOpen((prev) => !prev);
    }
  };

  const handleLanguageChange = useCallback(
    (event: React.MouseEvent<HTMLLIElement>): void => {
      const languageElement = (event.target as HTMLElement).closest(
        "[data-lang]"
      ) as HTMLElement | null;
      if (!languageElement) return;

      const lang = languageElement.dataset.lang as string;
      if (lang !== locale) {
        document.documentElement.setAttribute("lang", lang);
        dispatch(changeLocale(lang));
      }
      setPopupOpen(false);
    },
    [dispatch, locale]
  );

  useEffect(() => {
    if (!isPopupOpen) return;

    const abortController = new AbortController();

    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setPopupOpen(false);
      }
    };

    (root as HTMLElement).addEventListener("click", handleClickOutside, {
      signal: abortController.signal,
    });

    return () => abortController.abort();
  }, [isPopupOpen]);

  return (
    <li
      ref={dropdownRef}
      className={classNames("submenu__item dropdown", {
        dropdown_opened: isPopupOpen,
      })}
    >
      <button
        className={classNames("submenu__link", {
          "submenu__link--cursor-auto": isToggleDisabled,
        })}
        onClick={togglePopup}
        disabled={isToggleDisabled}
        aria-haspopup="true"
        aria-expanded={isPopupOpen}
      >
        <span className={`ico-flag ico-flag_${locale}`} aria-hidden="true" />
        {(appLanguage as any)[locale]}
        {!isToggleDisabled && (
          <span className="ico ico-b-arrow-down" aria-hidden="true" />
        )}
      </button>
      {isPopupOpen && (
        <div className="dropdown__drop lang__drop">
          <div className="dropdown__section">
            <ul className="list">
              {localeList.map((language) => (
                <li
                  key={language}
                  className="list__item list__btn"
                  data-lang={language}
                  onClick={handleLanguageChange}
                >
                  <span
                    className={`ico-flag ico-flag_${language}`}
                    aria-hidden="true"
                  />
                  {appLanguage[language]}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </li>
  );
};
